import { updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import UploadPhoto from "./UploadPhoto";

export default function UserImage({ handleEditClickMain, user, readOnly = false }) {
	const [showImageModal, setShowImageModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showPrivacyModal, setShowPrivacyModal] = useState(false);
	const [imageError, setImageError] = useState(false);
	const [photoChosen, setPhotoChosen] = useState(false);
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

	const hasExistingPhoto = !!(user.photoURL || user.photoMeta);

	const handleCloseEditModal = () => {
		setShowEditModal(false);
		setImageError(false);
	};
	const handleDeletePhoto = async () => {
		try {
			const filename = `profile_pictures/${auth.currentUser.uid}/profile_${auth.currentUser.uid}.webp`;
			const storageRef = ref(storage, filename);
			try {
				await deleteObject(storageRef);
			} catch (err) {
				if (err.code !== "storage/object-not-found") throw err;
			}

			await updateProfile(auth.currentUser, { photoURL: null });
			await setDoc(doc(db, "users", auth.currentUser.uid), { photoURL: null, photoMeta: null }, { merge: true });

			setShowDeleteConfirmModal(false);
			setShowImageModal(false);
			window.location.reload(); // ← refresh to show blank state
		} catch (err) {
			console.error("Error deleting photo:", err);
		}
	};

	return (
		<>
			{/* Profile photo or upload prompt */}
			{user.photoURL && !imageError ? (
				<div className="text-center">
					<div
						className="rounded-circle border border-secondary mx-auto"
						style={{ width: "150px", height: "150px", overflow: "hidden" }}
					>
						<img
							src={user.photoURL}
							alt="Uploaded"
							className="w-100 h-100"
							style={{ objectFit: "cover", cursor: readOnly ? "default" : "pointer" }}
							onClick={() => setShowImageModal(true)}
							onError={() => setImageError(true)}
						/>
					</div>
				</div>
			) : (
				!readOnly && (
					<div className="text-center my-2 p-2 border rounded mb-3">
						{!photoChosen && <i className="bi bi-person-circle mb-2" style={{ fontSize: "3rem", color: "#aaa" }}></i>}
						<UploadPhoto onPhotoCropped={() => setPhotoChosen(true)} hasExistingPhoto={hasExistingPhoto} />
					</div>
				)
			)}

			{/* Edit & info icons */}
			{!readOnly && (
				<div className="position-relative mt-2">
					<span
						className="position-absolute my-btn rounded-circle top-0 end-0 my-ice"
						style={{
							width: "30px",
							height: "30px",
							cursor: "pointer",
							transform: "translate(20%, -20%)",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							marginRight: "38px",
						}}
						onClick={() => setShowPrivacyModal(true)}
					>
						<i className="bi bi-info" style={{ fontSize: "2rem" }}></i>
					</span>

					<span
						className="position-absolute top-0 end-0 my-btn rounded-circle"
						style={{
							width: "30px",
							height: "30px",
							cursor: "pointer",
							transform: "translate(20%, -20%)",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
						onClick={handleEditClickMain}
					>
						<i className="bi bi-pencil-fill" style={{ fontSize: "1rem" }}></i>
					</span>
				</div>
			)}

			{/* View image modal */}
			{user.photoURL && (
				<>
					<div
						className={`modal fade ${showImageModal ? "show" : ""}`}
						style={{ display: showImageModal ? "block" : "none" }}
						tabIndex="-1"
						aria-hidden={!showImageModal}
						onClick={() => setShowImageModal(false)}
					>
						<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
							<div className="modal-content">
								<div className="modal-header">
									{!readOnly ? (
										<div className="d-flex gap-2">
											<button
												className="btn my-btn btn-sm align-items-center"
												onClick={() => {
													setShowImageModal(false);
													setShowEditModal(true);
												}}
											>
												<i className="bi bi-upload me-2"></i>
												Upload
											</button>
											<button
												className="btn btn-outline-danger btn-sm"
												onClick={() => setShowDeleteConfirmModal(true)}
												title="Delete photo"
											>
												<i className="bi bi-trash me-1"></i>
												Delete
											</button>
										</div>
									) : (
										user.displayName
									)}
									<button
										type="button"
										className="btn-close"
										aria-label="Close"
										onClick={() => setShowImageModal(false)}
									/>
								</div>
								<div className="modal-body text-center">
									<img
										src={user.photoURL}
										alt="User Profile"
										className="img-fluid"
										style={{ maxHeight: "90vh", maxWidth: "100%" }}
									/>
								</div>
							</div>
						</div>
					</div>
					{showImageModal && <div className="modal-backdrop fade show" onClick={() => setShowImageModal(false)} />}
				</>
			)}

			{/* Edit photo modal — passes hasExistingPhoto so delete button shows */}
			{!readOnly && (
				<Modal show={showEditModal} onHide={handleCloseEditModal} centered>
					<Modal.Header closeButton>
						<Modal.Title>Update Profile Picture</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<UploadPhoto onPhotoCropped={() => setPhotoChosen(true)} hasExistingPhoto={hasExistingPhoto} />
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseEditModal}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			)}

			{/* Privacy modal */}
			{!readOnly && (
				<Modal show={showPrivacyModal} onHide={() => setShowPrivacyModal(false)} centered>
					<Modal.Header closeButton>
						<Modal.Title>Privacy</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<ul>
							<li>Your Email and Telephone will NOT be visible to other users.</li>
							<li>
								During 'interaction' you can choose to share your contact information with another user at an event.
							</li>
							<li>
								Every other information you provide will be visible to other users, including your profile picture,
								name, and interests (we will use this to create icebreaker questions).
							</li>
							<li>
								How other users will see your profile:
								<br />
								<Button as={Link} to={`/profile/${user.uid}`} className="mt-2 p-2 my-btn">
									<i className="bi bi-person-circle me-2"></i> Preview
								</Button>
							</li>
						</ul>
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => setShowPrivacyModal(false)}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			)}
			{/* Delete confirmation modal */}
			<Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete Profile Photo</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">
					<i className="bi bi-exclamation-triangle text-danger mb-3" style={{ fontSize: "3rem" }}></i>
					<p className="mb-0">Are you sure you want to delete your profile photo?</p>
					<small className="text-muted">This cannot be undone.</small>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handleDeletePhoto}>
						<i className="bi bi-trash me-1"></i>Yes, delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}
